import React, { useState } from 'react';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  ClockIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import { ClipLoader } from 'react-spinners';
import { CONTACT_INFO, BUSINESS_INFO } from '../constants/contact';
import { useSnackbar } from '../contexts/SnackbarContext';
import useSEO from '../hooks/useSEO';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function Contact() {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.name || !formData.email || !formData.message) {
      showSnackbar('Please fill in all required fields.', 'error');
      setIsSubmitting(false);
      return;
    }

    const contactData = {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message
    };

    try {
      const res = await fetch('/.netlify/functions/sendContactEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData)
      });
      
      let responseData;
      try {
        responseData = await res.json();
      } catch (parseError) {
        responseData = { error: 'Invalid response format' };
      }
      
      if (res.ok) {
        showSnackbar('Message sent successfully!', 'success');
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        console.error('Contact submission failed:', responseData);
        showSnackbar(responseData.error || 'Failed to send message', 'error');
      }
    } catch (err: any) {
      console.error('Network error:', err);
      showSnackbar('Network error - please check your connection', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const contactInfo = [
    {
      icon: MapPinIcon,
      title: 'Address',
      details: [CONTACT_INFO.address.village, CONTACT_INFO.address.district, `${CONTACT_INFO.address.province}, ${CONTACT_INFO.address.country}`]
    },    {
      icon: PhoneIcon,
      title: 'Phone',
      details: [CONTACT_INFO.phone]
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      details: [CONTACT_INFO.email]
    },
    {
      icon: ClockIcon,
      title: 'Response Time',
      details: [`We typically respond within ${CONTACT_INFO.businessHours.responseTime}`, 'For urgent matters, please call']
    }  ];

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "LodgingBusiness",
      "name": "Bethanchok Homestay",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bethanchok",
        "addressRegion": "Central Nepal", 
        "addressCountry": "Nepal"
      },      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": CONTACT_INFO.phone,
        "email": CONTACT_INFO.email,
        "contactType": "Reservations",
        "availableLanguage": ["English", "Nepali"]
      }
    }  };

  // SEO Hook
  useSEO({
    title: "Contact Bethanchok Homestay | Book Your Nepal Stay | Get in Touch",
    description: "Contact Bethanchok Homestay for bookings and inquiries. Located in Bethanchok village, Nepal. Phone, email, and contact form available. Plan your authentic Nepal homestay experience today!",
    keywords: "contact Bethanchok Homestay, Nepal homestay booking, Bethanchok contact information, Nepal village stay contact, book Nepal homestay, Bethanchok phone number, Nepal tourism contact",
    image: "https://bethanchokstay.com/images/contact/contact-banner.jpg",
    url: "https://bethanchokstay.com/contact",
    structuredData: contactStructuredData
  });

  return (
    <div className="bg-white py-24 sm:py-32 page-transition">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Get in Touch</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Contact Us
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            Have questions about your stay? Want to learn more about our services? 
            We're here to help and would love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                We're Here to Help
              </h3>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Whether you're planning your first visit to Nepal or you're a returning guest, 
                our team is ready to assist you with any questions about accommodations, 
                activities, or local attractions.
              </p>
            </div>

            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                      <item.icon className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-neutral-900">{item.title}</h4>
                    {item.details.map((detail, idx) => (
                      <p key={idx} className="text-neutral-600 text-sm">{detail}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>            {/* Map */}
            <div className="mt-8">
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">Find Us</h4>
              <div className="aspect-video rounded-lg overflow-hidden bg-neutral-100">                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1000!2d${CONTACT_INFO.coordinates.lng}!3d${CONTACT_INFO.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2snp!4v1234567890123`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bethanchok Homestay Location"
                />
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-neutral-50 rounded-2xl p-8">
            {isSubmitted ? (
              <div className="text-center">
                <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
                <h3 className="text-xl font-bold text-neutral-900 mb-2">Message Sent!</h3>
                <p className="text-neutral-600 mb-6">
                  Thank you for contacting us. We'll get back to you soon.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="rounded-lg bg-primary-600 px-6 py-2 text-sm font-semibold text-white hover:bg-primary-500 transition-colors duration-200"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-neutral-900 mb-6">Send Us a Message</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-neutral-900 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-neutral-900 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-neutral-900 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      id="message"
                      rows={5}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isSubmitting ? (
                      <>
                        <ClipLoader color="white" size={16} className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
